import { useCallback, useMemo, useState } from 'react'

import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'
import { useNavigate } from 'react-router-dom'

import ChevronLeftIcon from '@/assets/icons/icon-chevron-left.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker'
import { SearchInput } from '@/components/ui/search-input.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { $creators, $isCreatorProcessCompleted } from '@/models/creators.ts'
import { useRestrictedWordLogs } from '@/modules/restricted-words/hooks/use-restricted-word-logs.ts'
import { RestrictedWordsLogsTable } from '@/modules/restricted-words/ui/restricted-words-logs-table.tsx'
import { useFetchTeamTable } from '@/modules/team/hooks/use-fetch-team-table.tsx'
import { debounce } from '@/utils/helpers.ts'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'

export const RestrictedWordsLogsView = () => {
  const creators = useUnit($creators)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const { data: members, isFetching: isFetchingTeam } = useFetchTeamTable()
  const { pagination, setPagination } = usePagination()
  const [selectedCreatorId, setSelectedCreatorId] = useState('all')
  const [selectedMemberId, setSelectedMemberId] = useState('all')
  const [selectedDate, setSelectedDate] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })
  const [searchValue, setSearchValue] = useState('')
  const [searchText, setSearchText] = useState('')
  const {
    restrictedWordLogsResponse,
    isRestrictedWordLogsLoading,
    isRestrictedWordLogsFetching,
  } = useRestrictedWordLogs({
    pagination,
    creatorId: selectedCreatorId === 'all' ? undefined : selectedCreatorId,
    memberId: selectedMemberId === 'all' ? undefined : selectedMemberId,
    word: searchValue,
    date: selectedDate,
  })

  const navigate = useNavigate()
  const handleBackClick = () => navigate(-1)

  const handleDateChange = useCallback((date: DateRange) => {
    setSelectedDate(date)
  }, [])
  const handleSearchValueChange = useMemo(
    () =>
      debounce((val: string) => {
        setSearchValue(val)
      }, 600),
    [],
  )

  const handleSearchChange = useCallback(
    (val: string) => {
      setSearchText(val)
      handleSearchValueChange(val)
    },
    [handleSearchValueChange],
  )

  return (
    <Page
      title="Blocked Messages Log"
      above={
        <Button
          onClick={handleBackClick}
          variant="ghost"
          className="flex items-center justify-start pl-0"
        >
          <ChevronLeftIcon className="mr-2" /> Back to Blacklisted Words
        </Button>
      }
    >
      <div className="mb-5 flex flex-col justify-between lg:flex-row">
        <div className="flex flex-col lg:flex-row">
          <div className="mr-0 w-full lg:mr-4 lg:w-[200px]">
            <Select
              disabled={!isCreatorProcessCompleted}
              value={selectedCreatorId}
              onValueChange={setSelectedCreatorId}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">All Creators</SelectItem>
                  {creators.map((c) => (
                    <SelectItem
                      value={c.id.toString()}
                      key={c.id}
                    >
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="mr-0 mt-2 w-full lg:mr-4 lg:mt-0 lg:w-[200px]">
            <Select
              disabled={isFetchingTeam}
              value={selectedMemberId}
              onValueChange={setSelectedMemberId}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">All Members</SelectItem>
                  {members?.map((m) => (
                    <SelectItem
                      value={m.userId.toString()}
                      key={m.id}
                    >
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div>
            <CalendarRangeDatePicker
              className="w-full sm:max-w-[204px]"
              date={{ to: selectedDate.to, from: selectedDate.from }}
              setDate={handleDateChange}
              maxDateRange={{
                to: new Date(),
                from: new Date(new Date().getFullYear() - 1, 1, 1),
              }}
            />
          </div>
        </div>
        <div className="mt-2 lg:mt-0">
          <SearchInput
            className="w-full lg:w-[unset]"
            placeholder="Search by blacklisted word"
            value={searchText}
            onInputChange={handleSearchChange}
          />
        </div>
      </div>

      {isRestrictedWordLogsLoading || isRestrictedWordLogsFetching ? (
        <div>
          <Skeleton className="mb-2 h-[48px] w-full" />
          <Skeleton className="h-[300px] w-full" />
        </div>
      ) : (
        <RestrictedWordsLogsTable
          pagination={pagination}
          setPagination={setPagination}
          data={restrictedWordLogsResponse?.content ?? []}
          pageCount={restrictedWordLogsResponse?.totalPages}
        />
      )}
    </Page>
  )
}
